//src/components/dashboard/DashboardNavbar.js
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  MdChevronLeft,
  MdChevronRight,
  MdKeyboardArrowDown,
  MdLogout,
  MdMenu,
  MdOpenInNew,
  MdPerson,
} from 'react-icons/md';

import { Link } from 'react-router-dom';
import { useUserProfile } from '../../hooks/useUserProfile';
import '../../styles/dashboard-notification.css';
import { clearAuth, getAuthUser } from '../../utils/auth';
import {
  clearProfileCache,
  getProfileAvatarUrl,
  getProfileDisplayName,
  getProfileSubscriptionLabel,
  getProfileSubscriptionStatusLabel,
} from '../../utils/profile';

const DashboardNavbar = ({ pageTitle, onMenuClick, onToggleSidebarCollapse, sidebarCollapsed }) => {
    const authUser = useMemo(() => getAuthUser() || {}, []);
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    const {
        profile,
        avatarUrl: profileAvatarUrl,
        displayName: profileDisplayName,
        loading: profileLoading,
        isFetching: profileIsFetching,
    } = useUserProfile({
        enabled: true,
    });

    const user = useMemo(
        () => ({
            ...(authUser || {}),
            ...(profile || {}),
        }),
        [authUser, profile]
    );

    const avatarUrl = getProfileAvatarUrl(user) || profileAvatarUrl || '';
    const displayName = getProfileDisplayName(user) || profileDisplayName || 'User';

    const planName = getProfileSubscriptionLabel(user);
    const planStatus = getProfileSubscriptionStatusLabel(user);

    const quota = profile?.subscription?.quota?.properties ?? null;

    const quotaLabel = useMemo(() => {
        if (!quota) return '';

        if (quota.is_unlimited) {
            return 'Kuota: Unlimited';
        }

        if (quota.remaining === null || quota.remaining === undefined) {
            return `Kuota: ${quota.used ?? 0}`;
        }

        return `Kuota: ${quota.remaining} tersisa`;
    }, [quota]);

    const handleLogout = () => {
        clearAuth();
        clearProfileCache();
        window.location.replace('/');
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!ref.current?.contains(e.target)) {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <header className={`dashboard-navbar ${sidebarCollapsed ? 'is-collapsed' : ''}`}>
            <div className="dashboard-navbar__left">
                <button
                    type="button"
                    className="dashboard-navbar__menu-btn"
                    onClick={onMenuClick}
                    aria-label="Buka sidebar"
                >
                    <MdMenu size={24} />
                </button>

                <button
                    type="button"
                    className="dashboard-navbar__collapse-btn"
                    onClick={onToggleSidebarCollapse}
                    aria-label={sidebarCollapsed ? 'Perbesar sidebar' : 'Minimize sidebar'}
                    title={sidebarCollapsed ? 'Perbesar sidebar' : 'Minimize sidebar'}
                >
                    {sidebarCollapsed ? <MdChevronRight size={22} /> : <MdChevronLeft size={22} />}
                </button>

                <div>
                    <h2 className="dashboard-navbar__title">{pageTitle || 'Dashboard'}</h2>
                </div>
            </div>

            <div className="dashboard-navbar__right">
                <a
                    href="/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="dashboard-navbar__site-cta"
                    aria-label="Buka halaman utama website"
                >
                    <MdOpenInNew size={18} />
                    <span>Lihat Website</span>
                </a>

                <div ref={ref} className="dashboard-navbar__user">
                    <button
                        type="button"
                        onClick={() => setOpen((prev) => !prev)}
                        className="dashboard-navbar__user-btn"
                        aria-expanded={open}
                        aria-busy={profileLoading || profileIsFetching}
                    >
                        <div className="dashboard-navbar__avatar">
                            {avatarUrl ? (
                                <img src={avatarUrl} alt={displayName || 'User'} />
                            ) : (
                                displayName?.charAt(0)?.toUpperCase() || 'U'
                            )}
                        </div>

                        <div className="dashboard-navbar__user-text">
                            <span className="dashboard-navbar__username">{displayName || 'User'}</span>
                            <div className="dashboard-navbar__meta">
                                <span className="dashboard-navbar__subscription-badge dashboard-navbar__subscription-badge--plan">
                                    {planName}
                                </span>

                                <span
                                    className={`dashboard-navbar__subscription-badge ${
                                        planStatus === 'Aktif'
                                            ? 'dashboard-navbar__subscription-badge--active'
                                            : 'dashboard-navbar__subscription-badge--inactive'
                                    }`}
                                >
                                    {planStatus}
                                </span>

                                {quotaLabel && (
                                    <span className="dashboard-navbar__subscription-badge dashboard-navbar__subscription-badge--quota">
                                        {quotaLabel.replace('Kuota: ', '')}
                                    </span>
                                )}
                            </div>
                        </div>

                        <MdKeyboardArrowDown size={20} />
                    </button>

                    {open && (
                        <div className="dashboard-navbar__dropdown">
                            <Link
                                to="/dashboard/profile"
                                className="dashboard-navbar__dropdown-item"
                                onClick={() => setOpen(false)}
                            >
                                <MdPerson size={16} />
                                <span>Profile</span>
                            </Link>

                            <button
                                type="button"
                                onClick={handleLogout}
                                className="dashboard-navbar__dropdown-item dashboard-navbar__dropdown-item--danger"
                            >
                                <MdLogout size={16} />
                                <span>Logout</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};

export default DashboardNavbar;
